import { useEffect, useState } from "react";
import { Button, ErrorNote, Spinner, toast } from "../components/ui";
import { api, useApi } from "../lib/api";

interface Settings {
  modelVersion?: string;
  modelFamily?: string;
  maxResponseTokens?: number;
  temperature?: number;
}
interface Answer {
  settings: Settings;
  defaults: Required<Settings>;
  maxResponseTokensCeiling: number;
}

/** Tenant admin: RADAR's model settings (design §15.4). Blank uses the platform default; a report can still override. */
export default function RadarSettings() {
  const { data, error } = useApi<Answer>("/admin/radar/settings");
  const [f, setF] = useState({ modelVersion: "", modelFamily: "", maxResponseTokens: "", temperature: "" });
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  useEffect(() => {
    if (!data) return;
    const s = data.settings;
    setF({ modelVersion: s.modelVersion ?? "", modelFamily: s.modelFamily ?? "", maxResponseTokens: s.maxResponseTokens?.toString() ?? "", temperature: s.temperature?.toString() ?? "" });
  }, [data]);
  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!data) return <Spinner />;
  const save = async () => {
    setBusy(true);
    setSaveError(null);
    try {
      await api("/admin/radar/settings", { method: "PUT", body: f });
      toast("RADAR settings saved");
    } catch (e) {
      setSaveError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const input = "w-full h-9 rounded-lg border border-line bg-surface px-3 text-sm";
  const field = (k: keyof typeof f, label: string, hint: string, type = "text") => (
    <label className="block text-sm">
      <span className="text-ink-2 font-medium">{label}</span>
      <input className={input} type={type} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={`Default: ${data.defaults[k]}`} />
      <span className="block text-xs text-muted">{hint}</span>
    </label>
  );
  return (
    <div className="max-w-xl">
      <h1 className="text-2xl font-bold text-ink mb-1">RADAR settings</h1>
      <p className="text-sm text-muted mb-6">The Infor GenAI model your organization's payroll variance reports use. Leave a field blank to use the platform default. Someone starting a report can still choose different values for that report.</p>
      <div className="space-y-4">
        {saveError && <ErrorNote>{saveError}</ErrorNote>}
        {field("modelVersion", "Model version", "The model Infor GenAI runs, for example claude-sonnet-4-6.")}
        {field("modelFamily", "Model family", "Usually CLAUDE.")}
        {field("maxResponseTokens", "Max response tokens", `The longest answer the model may write, up to ${data.maxResponseTokensCeiling}.`, "number")}
        {field("temperature", "Temperature", "From 0 to 1. Lower is more consistent.", "number")}
        <Button variant="primary" busy={busy} onClick={() => void save()}>
          Save
        </Button>
      </div>
    </div>
  );
}
